import { useState } from 'react'

import type { StatusLogFiltersInput } from 'types/graphql'

import LeadsByPeriodHeatmap from './LeadsByPeriodHeatmap'
import PipelineOutcomesTable from './PipelineOutcomesTable'
import SourceBreakdownCard from './SourceBreakdownCard'

export interface MarketingTabProps {
  filters: StatusLogFiltersInput
  /**
   * Bumped by the page-level Refresh button. Each self-fetching card watches
   * this and calls its own `refetch()` when the value changes — keeps the
   * Refresh button in sync with the Marketing tab without lifting queries up.
   */
  refreshKey: number
}

/**
 * Composed Marketing tab for AnalyticsPageV2. Reads the same filter shape that
 * the hero KPIs use, plumbs it down to three self-fetching cards.
 *
 * Layout (top to bottom):
 *   1. Source breakdown — combined per-source table (Leads / Hires / Conv % /
 *      Time to hire) with inline volume bars, plus the Budget / cost columns
 *      and the "Manage budgets" entry point for the monthly ledger.
 *   2. Pipeline Outcomes — the funnel + expandable reasons.
 *   3. Leads by Source over time — heatmap or per-source line chart.
 *
 * The blended "Marketing Spend" band (MarketingSpendBandCard) is built and
 * tested but deliberately not mounted yet — budgets are surfaced through the
 * per-source table for now. Drop it back in above SourceBreakdownCard to
 * restore it.
 *
 * "Snapshot → why → trend" reading order: see `playground-analytics-redesign.html`
 * for the visual spec.
 */
const MarketingTab = ({ filters, refreshKey }: MarketingTabProps) => {
  // Saving a budget changes the cost columns in Pipeline by Source, including
  // any drill-down rows already expanded. Those children are cached in the
  // card's own state, so refetching the parent query isn't enough — bumping
  // the same key Refresh uses invalidates them too.
  const [budgetVersion, setBudgetVersion] = useState(0)
  const cardRefreshKey = refreshKey + budgetVersion

  return (
    <div className="flex flex-col gap-4">
      <SourceBreakdownCard
        filters={filters}
        refreshKey={cardRefreshKey}
        onBudgetSaved={() => setBudgetVersion((v) => v + 1)}
      />
      <PipelineOutcomesTable filters={filters} refreshKey={refreshKey} />
      <LeadsByPeriodHeatmap filters={filters} refreshKey={refreshKey} />
    </div>
  )
}

export default MarketingTab
